import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useLocation } from 'react-router-dom';
import { Mail, Phone, MapPin, Send, CheckCircle, FileText, Building2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { companyInfo, inquiryCategories } from '../data/mockData';
import { TOPIC_TO_INQUIRY_TYPE } from '../utils/vikkiEmailDraft';

const Contact = () => {
  const location = useLocation();
  const formSectionRef = useRef(null);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    organization: '',
    inquiryType: '',
    message: ''
  });

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  /* ----- VIKKI pre-fill: read ?topic=&subject=&message= from URL ----- */
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const topic   = params.get('topic');
    const subject = params.get('subject');
    const message = params.get('message');
    if (!topic && !subject && !message) return;

    const mappedType = TOPIC_TO_INQUIRY_TYPE[topic];
    const resolvedInquiryType = (mappedType && inquiryCategories.includes(mappedType)) ? mappedType : '';

    setFormData((prev) => ({
      ...prev,
      // Only overwrite empty fields so a visitor cannot accidentally lose typed input
      inquiryType: prev.inquiryType || resolvedInquiryType,
      message:     prev.message     || (message ? String(message).slice(0, 2000) : ''),
    }));

    // Scroll to the form so the visitor sees their pre-filled message
    if (formSectionRef.current) {
      requestAnimationFrame(() => {
        formSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }

    if (message || subject) {
      toast.success('VIKKI pre-filled your inquiry. Review and add your contact details before sending.');
    }
  }, [location.search]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (value) => {
    // Guard against Radix Select firing onValueChange('') during controlled-state
    // transitions — that would wipe a VIKKI pre-filled inquiryType.
    if (!value) return;
    setFormData(prev => ({ ...prev, inquiryType: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.inquiryType || !formData.message) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    try {
      const apiBase = process.env.REACT_APP_BACKEND_URL || '';
      const resp = await fetch(`${apiBase}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, website: e.target.elements?.website?.value || '' }),
      });

      const data = await resp.json().catch(() => ({}));

      if (!resp.ok) {
        toast.error(data.error || 'Could not send your message. Please email contact@salartechnologies.com.');
        return;
      }

      toast.success('Your inquiry has been submitted successfully — we will respond within 1–2 business days.');
      setIsSubmitted(true);

      setTimeout(() => {
        setFormData({ name: '', email: '', phone: '', organization: '', inquiryType: '', message: '' });
        setIsSubmitted(false);
      }, 4000);
    } catch (err) {
      console.error('Contact submit failed:', err);
      toast.error('Network error. Please email contact@salartechnologies.com.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-transparent">
      <Helmet>
        <title>Contact SALAR Technologies LLC | Start an Engineering Project</title>
        <meta name="description" content="Contact SALAR Technologies LLC to request an engineering assessment, consulting project, RFI/RFQ/RFP response, semiconductor supply-chain analysis, electronics reliability review, AI infrastructure assurance, or technical due diligence." />
        <meta property="og:title" content="Contact SALAR Technologies LLC" />
        <meta property="og:description" content="Request an engineering project, assessment, RFI/RFQ/RFP response, government or defense engagement, technical due diligence, partnership, or research collaboration." />
        <meta property="og:url" content="https://www.salartechnologies.com/contact" />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://www.salartechnologies.com/contact" />
        <meta name="robots" content="index, follow" />
      </Helmet>
      {/* Hero */}
      <section
        className="text-white py-20"
        style={{
          backgroundImage: "linear-gradient(rgba(10,22,40,0.65), rgba(10,22,40,0.9)), url('/img/ai_fabric_hero.jpg')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
        data-testid="contact-hero"
      >
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl lg:text-5xl font-bold mb-6">Contact SALAR Technologies LLC</h1>
            <p className="text-xl text-slate-300 leading-relaxed">
              Reach out for government, defense, partnership, or technical collaboration inquiries.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Content */}
      <section ref={formSectionRef} className="py-16">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Contact Information */}
            <div className="lg:col-span-1 space-y-6">
              <Card className="border-blue-500/30">
                <CardHeader>
                  <CardTitle className="text-white">Contact Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <Mail className="text-white flex-shrink-0 mt-1" size={20} />
                    <div>
                      <p className="text-sm font-semibold text-blue-100 mb-1">General Contact</p>
                      <a 
                        href={`mailto:${companyInfo.emails.general}`}
                        className="text-sm text-blue-200 hover:text-white transition-colors break-all"
                      >
                        {companyInfo.emails.general}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3">
                    <Mail className="text-white flex-shrink-0 mt-1" size={20} />
                    <div>
                      <p className="text-sm font-semibold text-blue-100 mb-1">Direct Contact</p>
                      <a 
                        href={`mailto:${companyInfo.emails.direct}`}
                        className="text-sm text-blue-200 hover:text-white transition-colors break-all"
                      >
                        {companyInfo.emails.direct}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3" data-testid="contact-licensing-block">
                    <Mail className="text-cyan-300 flex-shrink-0 mt-1" size={20} />
                    <div>
                      <p className="text-sm font-semibold text-cyan-200 mb-1">Patent Licensing</p>
                      <a 
                        href="mailto:licensing@salartechnologies.com"
                        className="text-sm text-cyan-300 hover:text-cyan-200 transition-colors break-all"
                      >
                        licensing@salartechnologies.com
                      </a>
                      <p className="text-[11px] text-blue-300/70 mt-1">
                        For patent licensing inquiries, please use <Link to="/licensing/request" className="text-cyan-300 hover:text-cyan-200 underline">our request form</Link>.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3">
                    <Phone className="text-white flex-shrink-0 mt-1" size={20} />
                    <div>
                      <p className="text-sm font-semibold text-blue-100 mb-1">Phone</p>
                      <a 
                        href={`tel:${companyInfo.phone}`}
                        className="text-sm text-blue-200 hover:text-white transition-colors"
                      >
                        {companyInfo.phone}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3">
                    <MapPin className="text-white flex-shrink-0 mt-1" size={20} />
                    <div>
                      <p className="text-sm font-semibold text-blue-100 mb-1">Location</p>
                      <p className="text-sm text-blue-200">{companyInfo.location}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-cyan-500/30 bg-cyan-950/20 backdrop-blur-sm text-white">
                <CardHeader>
                  <CardTitle className="text-white">Project Intake</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-slate-300 mb-3">
                    Buyers, agencies, primes, manufacturers, investors, and engineering teams can submit scoped project requests directly through this form.
                  </p>
                  <a href="/request-engineering-project.html" className="text-sm font-semibold text-cyan-300 hover:text-cyan-200 underline">
                    See project types and what to include →
                  </a>
                </CardContent>
              </Card>

              <Card className="border-blue-500/30 bg-blue-950/30 backdrop-blur-sm text-white">
                <CardHeader>
                  <CardTitle className="text-white">Business Hours</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-slate-300">
                    We respond to inquiries within 1-2 business days.
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card className="border-blue-500/30">
                <CardHeader>
                  <CardTitle className="text-2xl text-white">Send Us an Inquiry</CardTitle>
                </CardHeader>
                <CardContent>
                  {isSubmitted ? (
                    <div className="py-12 text-center">
                      <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
                      <h3 className="text-2xl font-bold text-white mb-2">Thank You!</h3>
                      <p className="text-blue-100">
                        Your inquiry has been submitted. We'll get back to you shortly.
                      </p>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-6">
                      <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" style={{ position: 'absolute', left: '-10000px', width: 1, height: 1, overflow: 'hidden' }} />
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <Label htmlFor="name" className="text-blue-100">
                            Full Name <span className="text-red-500">*</span>
                          </Label>
                          <Input
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            placeholder="John Doe"
                            required
                            className="border-slate-300 focus:border-[#0a1628]"
                          />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="email" className="text-blue-100">
                            Email <span className="text-red-500">*</span>
                          </Label>
                          <Input
                            id="email"
                            name="email"
                            type="email"
                            value={formData.email}
                            onChange={handleChange}
                            placeholder="john@example.com"
                            required
                            className="border-slate-300 focus:border-[#0a1628]"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <Label htmlFor="phone" className="text-blue-100">Phone</Label>
                          <Input
                            id="phone"
                            name="phone"
                            type="tel"
                            value={formData.phone}
                            onChange={handleChange}
                            placeholder="(555) 123-4567"
                            className="border-slate-300 focus:border-[#0a1628]"
                          />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="organization" className="text-blue-100">Organization</Label>
                          <Input
                            id="organization"
                            name="organization"
                            value={formData.organization}
                            onChange={handleChange}
                            placeholder="Your Company"
                            className="border-slate-300 focus:border-[#0a1628]"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="inquiryType" className="text-blue-100">
                          Inquiry Type <span className="text-red-500">*</span>
                        </Label>
                        <Select
                          value={formData.inquiryType || undefined}
                          onValueChange={handleSelectChange}
                          required
                        >
                          <SelectTrigger
                            data-testid="contact-inquiry-type-trigger"
                            className="border-slate-300 focus:border-[#0a1628]"
                          >
                            <SelectValue placeholder="Select inquiry type" />
                          </SelectTrigger>
                          <SelectContent
                            data-testid="contact-inquiry-type-content"
                            className="bg-[#0F1A2B] border border-blue-500/40 text-blue-100"
                          >
                            {inquiryCategories.map((category) => (
                              <SelectItem
                                key={category}
                                value={category}
                                data-testid={`contact-inquiry-type-${category.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}
                                className="text-blue-100 focus:bg-cyan-600 focus:text-white data-[highlighted]:bg-cyan-600 data-[highlighted]:text-white data-[state=checked]:bg-cyan-700 data-[state=checked]:text-white"
                              >
                                {category}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="message" className="text-blue-100">
                          Message <span className="text-red-500">*</span>
                        </Label>
                        <Textarea
                          id="message"
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          placeholder="Tell us about your inquiry..."
                          rows={6}
                          required
                          className="border-slate-300 focus:border-[#0a1628] resize-none"
                        />
                      </div>

                      <Button 
                        type="submit" 
                        size="lg"
                        disabled={isSubmitting}
                        data-testid="contact-submit-btn"
                        className="w-full bg-blue-950/30 backdrop-blur-sm hover:bg-[#1e3a5f] disabled:opacity-60"
                      >
                        <Send className="mr-2" size={20} />
                        {isSubmitting ? 'Sending…' : 'Send Inquiry'}
                      </Button>
                    </form>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Options */}
      <section
        className="py-16 text-white"
        style={{
          backgroundImage: "linear-gradient(rgba(10,22,40,0.85), rgba(10,22,40,0.95)), url('/img/data_center_servers.jpg')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
        data-testid="contact-cta"
      >
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-6">Other Ways to Connect</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Request Consultation Card - Opens Email */}
              <a 
                href={`mailto:${companyInfo.emails.general}?subject=Request for Technical Consultation&body=I would like to discuss technical requirements and project collaboration.`}
                className="block"
              >
                <Card className="bg-blue-900/20 backdrop-blur-sm border-white/10 hover:bg-blue-800/30 hover:scale-105 transition-all duration-300 cursor-pointer h-full">
                  <CardContent className="pt-6 text-center">
                    <Mail className="w-12 h-12 mx-auto mb-3 text-cyan-400" />
                    <p className="text-white font-semibold mb-2 text-lg">Request a Consultation</p>
                    <p className="text-sm text-slate-300">Discuss technical requirements</p>
                  </CardContent>
                </Card>
              </a>

              {/* Partnership Inquiry Card - Opens Email */}
              <a 
                href={`mailto:${companyInfo.emails.general}?subject=Partnership Inquiry&body=I am interested in exploring collaboration opportunities with SALAR Technologies.`}
                className="block"
              >
                <Card className="bg-blue-900/20 backdrop-blur-sm border-white/10 hover:bg-blue-800/30 hover:scale-105 transition-all duration-300 cursor-pointer h-full">
                  <CardContent className="pt-6 text-center">
                    <Building2 className="w-12 h-12 mx-auto mb-3 text-blue-400" />
                    <p className="text-white font-semibold mb-2 text-lg">Partnership Inquiry</p>
                    <p className="text-sm text-slate-300">Explore collaboration opportunities</p>
                  </CardContent>
                </Card>
              </a>

              {/* Download Resources Card - Links to Documents Page */}
              <Link to="/documents" className="block">
                <Card className="bg-blue-900/20 backdrop-blur-sm border-white/10 hover:bg-blue-800/30 hover:scale-105 transition-all duration-300 cursor-pointer h-full">
                  <CardContent className="pt-6 text-center">
                    <FileText className="w-12 h-12 mx-auto mb-3 text-green-400" />
                    <p className="text-white font-semibold mb-2 text-lg">Download Resources</p>
                    <p className="text-sm text-slate-300">Access capability statements</p>
                  </CardContent>
                </Card>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
